// Radar analysis (its own page, beside Field cameras and Node control): what
// the server's analysis of the bursts says - our estimate of the distance to
// the surface beside the radar's own reading, dry, wet or unknown with a
// confidence and the reasons, every echo labelled - what a burst cost in
// energy (firmware .57), the node's empty-pipe profile, and the radar
// rich-data lines of the projection (Node Control, Before you apply). The
// server is the authority: these helpers only read it defensively and word
// it. Pure: no React, network or storage.
import { finite, formatMm as mm, timeOf } from "utils/radarDiagnostics";
import { burstEstimate, burstKind, readWhole, ruleSet } from "utils/radarBurst";
import {
  RICH_RULES,
  capturesPerWake,
  richEstimate,
  uploadExtras,
} from "utils/radarRich";
import {
  readMask,
  readRawAlt,
  readRawMode,
  streamEstimate,
} from "utils/radarStream";

const has = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

// ---- States and labels ----

export const STATES = {
  dry: { label: "Dry", color: "#9a6b2f", band: "#f4e9d8" },
  wet: { label: "Wet", color: "#1f6fd1", band: "#dce9fa" },
  unknown: { label: "Unknown", color: "#5f7682", band: "#edf2f4" },
};
// "dry", "wet" or "unknown" (also for a missing or unexpected value).
export function readState(value) {
  const s = String(value ?? "")
    .trim()
    .toLowerCase();
  return has(STATES, s) ? s : "unknown";
}

// The server's confidence, 0-100 (100 less a number of points for each
// reason), as a whole percentage; null without one.
export function confidencePct(value) {
  const v = finite(value);
  if (v === null) return null;
  return Math.round(Math.min(100, Math.max(0, v)));
}

// What our analysis calls each echo, with a colour that is never the only
// cue (the charts write the label beside it).
export const ECHO_LABELS = {
  water: {
    text: "Water",
    color: "#1f6fd1",
    meaning: "the water surface",
  },
  floor: {
    text: "Floor",
    color: "#8a5a2b",
    meaning: "the floor of the pipe, with no water over it",
  },
  // Analysis version 2: beyond the pipe's length (+3%), where the floor
  // cannot lie, so never the floor or water.
  below_floor: {
    text: "Below the floor",
    color: "#4d6b3c",
    meaning: "a longer bounce path or soil layering",
  },
  clutter: {
    text: "Clutter",
    color: "#7d4fa3",
    meaning: "a fixed echo in the pipe (the rim, a joint, the mount)",
  },
  harmonic: {
    text: "Harmonic",
    color: "#b4690e",
    meaning:
      "a copy of a strong echo at 2-4 times its distance (a double bounce, or clipping)",
  },
  near_field: {
    text: "Near field",
    color: "#b83280",
    meaning: "the radar's own leakage and its lens",
  },
  unknown: {
    text: "Unlabelled",
    color: "#7b919c",
    meaning: "an echo the analysis could not place",
  },
};
export const LABEL_ORDER = [
  "water",
  "floor",
  "below_floor",
  "clutter",
  "harmonic",
  "near_field",
  "unknown",
];
export function readLabel(value) {
  const s = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return has(ECHO_LABELS, s) ? s : "unknown";
}

// Why an echo is clutter: in the empty-pipe map, or at the same range in most
// of the node's recent bursts (fixed structure, without a map).
export const BASES = {
  map: "in the empty-pipe map",
  fixed_structure: "at the same range in most recent bursts (fixed structure)",
};
const reasonText = (r) =>
  typeof r === "string"
    ? r.trim()
    : r && typeof r === "object"
      ? String(r.text ?? r.message ?? r.reason ?? "").trim()
      : "";

// A burst's analysis as GET radar/bursts/ serves it (`analysis`), read
// defensively: {state, ourMm, confidence (%), why, reasons, radarMm, agrees,
// floorMm, expectedFloorMm, echoes [{mm, db, snr, label, bin, basis, approx}]
// nearest first, clipPct, flickerDb, noiseDb, method ("beat_signal" |
// "spectrum"), spectra, captures, clutterMap, history, profileAt, version,
// and with a soil reference on the node's profile depthMm and
// depthBelowTube (the water against the soil surface: utils/radarSoil)};
// null without one.
export function readAnalysis(a) {
  if (!a || typeof a !== "object") return null;
  const echoes = (Array.isArray(a.echoes) ? a.echoes : [])
    .map((e) => ({
      mm: finite(e?.mm),
      db: finite(e?.db),
      snr: finite(e?.snr),
      label: readLabel(e?.label),
      bin: finite(e?.bin),
      basis: has(BASES, String(e?.basis)) ? e.basis : null,
      approx: e?.approx === true,
    }))
    .filter((e) => e.mm !== null)
    .sort((x, y) => x.mm - y.mm);
  const text = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);
  return {
    state: readState(a.state),
    ourMm: finite(a.our_mm),
    confidence: confidencePct(a.confidence),
    why: text(a.why),
    reasons: (Array.isArray(a.reasons) ? a.reasons : [])
      .map(reasonText)
      .filter(Boolean),
    radarMm: finite(a.radar_mm),
    agrees: typeof a.agrees === "boolean" ? a.agrees : null,
    floorMm: finite(a.floor_mm),
    expectedFloorMm: finite(a.expected_floor_mm),
    echoes,
    clipPct: finite(a.clip_pct),
    flickerDb: finite(a.flicker_db),
    noiseDb: finite(a.noise_db),
    method: ["beat_signal", "spectrum"].includes(a.method) ? a.method : null,
    spectra: finite(a.spectra),
    captures: finite(a.captures),
    clutterMap: typeof a.clutter_map === "boolean" ? a.clutter_map : null,
    history: finite(a.history),
    profileAt: timeOf(a.profile_at) === null ? null : a.profile_at,
    version: a.version ?? null,
    // Firmware .62: the node's own estimator-v2 estimate of the burst's beat
    // signals (the median of its answers, how many) and its difference from
    // the server's leading edge of the same captures; a rich wake whose raw
    // captures the node held back (decided from the node's echo list:
    // estimator v2's method node_candidates, flagged for what only the beat
    // signal shows) and the wake's keep note.
    edge2Mm: finite(a.edge2_mm),
    edge2N: finite(a.edge2_n),
    edge2DiffMm: finite(a.edge2_diff_mm),
    heldBack: a.held_back === true,
    keep: a.keep && typeof a.keep === "object" ? a.keep : null,
    v2Method: text(a.v2?.method),
    v2Flags: (Array.isArray(a.v2?.flags) ? a.v2.flags : []).filter(
      (f) => typeof f === "string" && f,
    ),
    // The water against the soil surface (positive over it), and whether
    // the pipe is dry, the water below it: only with a soil reference.
    depthMm: finite(a.depth_mm),
    depthBelowTube: a.depth_below_tube === true,
  };
}

// Where a burst's analysis stands: "ready", "pending" (the server lists
// analysis null until it has analysed the burst; it does a few a request)
// or "none" (a server before analyses lists none at all).
export function analysisStatus(burst) {
  if (!burst || typeof burst !== "object" || !has(burst, "analysis"))
    return "none";
  return burst.analysis && typeof burst.analysis === "object"
    ? "ready"
    : "pending";
}

// The labelled echo nearest a distance, within `within` mm; null if none.
export function nearestEcho(echoes, distance, within = 50) {
  const at = finite(distance);
  if (at === null) return null;
  let best = null;
  for (const e of echoes || []) {
    const d = Math.abs(e.mm - at);
    if (d <= within && (!best || d < best.d)) best = { ...e, d };
  }
  return best;
}

// Within this the radar's reading and our estimate agree, as the server
// decides it (its agrees), when it does not say.
export const AGREE_MM = 30;
const RADAR_IS = {
  clutter: "a fixed echo of the empty pipe (clutter), not the surface",
  near_field:
    "in the near field: the radar's own leakage, or something on its lens",
  harmonic: "a harmonic: a copy of a strong echo at a multiple of its distance",
  below_floor:
    "below the floor: a longer bounce path or soil layering, beyond the pipe's length",
  floor: "the floor",
  water: "a water echo",
  unknown: "an echo our analysis could not place",
};
const ourWords = (a) =>
  a.state === "dry"
    ? `the floor at ${mm(a.ourMm)}, so the pipe is dry`
    : a.state === "wet"
      ? `the water surface at ${mm(a.ourMm)}`
      : `${mm(a.ourMm)}, without telling dry from wet`;

// The analysis in words: whether the radar's reading and our estimate
// agree and, where they do not, what the radar's reading is by our labels.
// -> {tone: "agree" | "disagree" | "none", text}, or null without analysis.
export function verdict(a, { tolerance = AGREE_MM } = {}) {
  if (!a) return null;
  const { radarMm: radar, ourMm: ours } = a;
  if (ours === null)
    return {
      tone: "none",
      text:
        radar === null
          ? "Neither the radar nor our analysis has a distance for this burst."
          : `The radar's own reading is ${mm(radar)}; our analysis found no echo it trusts as the water surface or the floor.`,
    };
  if (radar === null)
    return {
      tone: "none",
      text: `The radar gave no valid reading. Our estimate: ${ourWords(a)}.`,
    };
  const apart = Math.abs(radar - ours);
  const agrees = a.agrees ?? apart <= tolerance;
  if (agrees)
    return {
      tone: "agree",
      text: `The radar and our estimate agree${apart >= 0.5 ? ` within ${mm(apart)}` : ""}: ${ourWords(a)}.`,
    };
  const echo = nearestEcho(a.echoes, radar, tolerance * 1.5);
  return {
    tone: "disagree",
    text: `They disagree. The radar's ${mm(radar)} is ${echo ? RADAR_IS[echo.label] : "not an echo our analysis counts"}. Our estimate: ${ourWords(a)}.`,
  };
}

// The analysis's own sentence (why), else ours.
export const headline = (a) => a?.why || verdict(a)?.text || null;

// How the analysis was reached, in words: "From the beat signals of 2
// captures · with the empty-pipe map" or "From the spectra only (positions
// approximate) · without an empty-pipe map".
export function methodText(a) {
  if (!a) return "";
  const n = a.spectra;
  return [
    a.method === "beat_signal"
      ? "From the beat signals"
      : a.method === "spectrum"
        ? "From the spectra only: positions approximate (about a cell on node 1)"
        : null,
    n !== null && n !== undefined ? `${n} spectr${n === 1 ? "um" : "a"}` : null,
    a.clutterMap === true
      ? "with the empty-pipe map"
      : a.clutterMap === false
        ? "without an empty-pipe map"
        : null,
    a.history !== null && a.history !== undefined && a.history > 0
      ? `${a.history} earlier burst${a.history === 1 ? "" : "s"} of this node`
      : null,
    a.version !== null && a.version !== undefined
      ? `analysis version ${a.version}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

// ---- Energy (firmware .57) ----

// A burst's measured energy: duration (ms), current samples (n), mean and
// peak current (mA), charge (mAh), the current before the radar (base_ma),
// the solar current (si_ma) and the battery voltage (bv); or {usb: true}
// on USB power, where the node does not measure it. Null without either.
export function readEnergy(e) {
  if (!e || typeof e !== "object") return null;
  if (e.usb === true) return { usb: true };
  const out = {
    ms: finite(e.ms),
    n: finite(e.n),
    ma: finite(e.ma),
    maMax: finite(e.ma_max),
    mah: finite(e.mah),
    baseMa: finite(e.base_ma),
    siMa: finite(e.si_ma),
    bv: finite(e.bv),
  };
  return Object.values(out).some((v) => v !== null)
    ? { usb: false, ...out }
    : null;
}

const ma = (v) =>
  `${v.toLocaleString(undefined, { maximumFractionDigits: v < 10 ? 1 : 0 })} mA`;
// "0.42 mAh in 21.6 s · mean 72 mA, peak 180 mA · 45 mA before the radar ·
// solar 120 mA · battery 3.92 V · 86 samples"
export function energyText(energy) {
  const e = readEnergy(energy);
  if (!e) return "";
  if (e.usb) return "On USB power: the node does not measure a burst's energy.";
  const charge =
    e.mah !== null
      ? `${e.mah.toLocaleString(undefined, { maximumFractionDigits: e.mah < 1 ? 3 : 2 })} mAh${e.ms !== null ? ` in ${(e.ms / 1000).toFixed(1)} s` : ""}`
      : e.ms !== null
        ? `${(e.ms / 1000).toFixed(1)} s`
        : null;
  const current = [
    e.ma !== null ? `mean ${ma(e.ma)}` : null,
    e.maMax !== null ? `peak ${ma(e.maMax)}` : null,
  ]
    .filter(Boolean)
    .join(", ");
  return [
    charge,
    current || null,
    e.baseMa !== null ? `${ma(e.baseMa)} before the radar` : null,
    e.siMa !== null ? `solar ${ma(e.siMa)}` : null,
    e.bv !== null ? `battery ${e.bv.toFixed(2)} V` : null,
    e.n !== null ? `${Math.round(e.n).toLocaleString()} samples` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

// ---- The timeline ----

// Rich readings bring one burst a wake (about 144 a day on a 10-minute
// wake), so over 7 and 30 days the page asks the server to thin the points
// out (?max_points=: of each spell of time it keeps the bursts with the
// lowest and the highest of our distance and the radar's); a day keeps every
// wake.
export const ANALYSIS_MAX_POINTS = 720;
export const analysisMaxPoints = (days) =>
  days > 1 ? ANALYSIS_MAX_POINTS : null;

// GET radar/analysis/'s points, oldest first: {t, ms, burstId, kind ("rich"
// for a rich reading, else "burst"), radarMm, ourMm, state, confidence (%),
// heldBack (firmware .62: a rich wake whose raw captures the node held back,
// decided from the node's echo list), edge2Mm and edge2DiffMm (the node's own
// estimator-v2 estimate and its difference from ours on the same captures)};
// points without a valid time are left out. A thinned-out answer's points are
// bursts too, a subset of them.
export function analysisPoints(data) {
  const list = Array.isArray(data?.points) ? data.points : [];
  return list
    .map((p) => ({
      t: p?.t ?? null,
      ms: timeOf(p?.t),
      burstId: p?.burst_id ?? null,
      kind: burstKind(p),
      radarMm: finite(p?.radar_mm),
      ourMm: finite(p?.our_mm),
      state: readState(p?.state),
      confidence: confidencePct(p?.confidence),
      heldBack: p?.held_back === true,
      edge2Mm: finite(p?.edge2_mm),
      edge2DiffMm: finite(p?.edge2_diff_mm),
      depthMm: finite(p?.depth_mm),
      depthBelowTube: p?.depth_below_tube === true,
    }))
    .filter((p) => p.ms !== null)
    .sort((a, b) => a.ms - b.ms);
}

// Whether the server thinned the points out (`decimated`), and the analysed
// bursts of the window (its `total`, the points before max_points; else as
// many as the points). -> {decimated, bursts, points}
export function analysisThinning(data, points) {
  const shown = (points || []).length;
  const total = finite(data?.total);
  const bursts = total !== null && total >= shown ? Math.round(total) : shown;
  return {
    decimated: data?.decimated === true || bursts > shown,
    bursts,
    points: shown,
  };
}

// The points as runs of one state for the timeline's band: each point
// covers half the way to its neighbours, at most `reach` either side (a
// point at an end the same as on its other side), so a run spans midway to
// midway and a long gap leaves the band empty. -> [{state, from, to}]
export function stateRuns(points, { reach = 2 * 3600000 } = {}) {
  const runs = [];
  (points || []).forEach((p, i) => {
    const prev = points[i - 1];
    const next = points[i + 1];
    const left = prev ? Math.min(reach, (p.ms - prev.ms) / 2) : null;
    const right = next ? Math.min(reach, (next.ms - p.ms) / 2) : null;
    const from = p.ms - (left ?? right ?? reach);
    const to = p.ms + (right ?? left ?? reach);
    const last = runs[runs.length - 1];
    if (last && last.state === p.state && from - last.to <= 1) last.to = to;
    else runs.push({ state: p.state, from, to });
  });
  return runs;
}

// ---- The empty-pipe profile ----

// GET/POST radar/profile/'s answer -> the profile, or null when there is
// none ({profile: null}, null or {}).
export function readProfile(data) {
  if (!data || typeof data !== "object") return null;
  const p = has(data, "profile") ? data.profile : data;
  return p && typeof p === "object" && Object.keys(p).length ? p : null;
}

const count = (v) => (Array.isArray(v) ? v.length : finite(v));
const timeText = (v) => (timeOf(v) === null ? null : v);
// An earlier profile the server keeps (radar/profile/'s revisions, newest
// first): one a change replaced (reason "replaced": Undo puts back the newest
// of these) or one an undo replaced ("undone"), with its map's bursts and
// window, its lengths, when it was saved and who replaced it.
function readRevision(r) {
  if (!r || typeof r !== "object" || timeOf(r.replaced_at) === null)
    return null;
  const bursts = count(r.clutter_n);
  return {
    replacedAt: r.replaced_at,
    reason: r.reason === "undone" ? "undone" : "replaced",
    updatedAt: timeText(r.profile_updated_at),
    from: timeText(r.commissioned_from),
    to: timeText(r.commissioned_to),
    bursts,
    commissioned:
      (bursts !== null && bursts > 0) || timeOf(r.commissioned_from) !== null,
    pipeLengthMm: finite(r.pipe_length_mm),
    expectedFloorMm: finite(r.expected_floor_mm),
    nearMm: finite(r.near_mm),
    maxWaterMm: finite(r.max_water_mm),
    soilAboveFloorMm: finite(r.soil_above_floor_mm),
    by:
      typeof r.replaced_by === "string" && r.replaced_by ? r.replaced_by : null,
  };
}
// What the page shows of a profile (GET/POST radar/profile/: pipe_length_mm,
// expected_floor_mm, clutter_n, commissioned_from, commissioned_to, notes,
// updated_at, clutter_db, quality {bursts, noise_db, echoes [{mm, db,
// snr}], floor {mm, db, snr} or null, warnings}, from the server that keeps
// them can_undo and revisions and, from the backend's .62 support, the node's
// own search window: near_mm and max_water_mm, null for the global setting;
// from its soil support the soil surface over the pipe's floor,
// soil_above_floor_mm, and below the radar, soil_air_mm, each null for none,
// soilKept saying the server keeps it), read defensively.
export function profileFacts(p) {
  if (!p) return null;
  const q = p.quality && typeof p.quality === "object" ? p.quality : null;
  const echo = (e) =>
    e && finite(e.mm) !== null
      ? { mm: finite(e.mm), db: finite(e.db), snr: finite(e.snr) }
      : null;
  const bursts = count(p.clutter_n) ?? (q ? count(q.bursts) : null);
  return {
    updatedAt: timeText(p.updated_at),
    from: timeText(p.commissioned_from),
    to: timeText(p.commissioned_to),
    commissioned:
      Array.isArray(p.clutter_db) ||
      (bursts !== null && bursts > 0) ||
      timeOf(p.commissioned_from) !== null,
    bursts,
    pipeLengthMm: finite(p.pipe_length_mm),
    expectedFloorMm: finite(p.expected_floor_mm),
    nearMm: finite(p.near_mm),
    maxWaterMm: finite(p.max_water_mm),
    soilAboveFloorMm: finite(p.soil_above_floor_mm),
    soilAirMm: finite(p.soil_air_mm),
    soilKept: has(p, "soil_above_floor_mm"),
    notes: typeof p.notes === "string" ? p.notes.trim() : "",
    quality: q && {
      bursts: count(q.bursts),
      noiseDb: finite(q.noise_db),
      echoes: (Array.isArray(q.echoes) ? q.echoes : [])
        .map(echo)
        .filter(Boolean),
      floor: echo(q.floor),
      warnings: (Array.isArray(q.warnings) ? q.warnings : [])
        .map(reasonText)
        .filter(Boolean),
    },
    // Earlier profiles, newest first, and whether Undo has one to put back.
    keepsRevisions:
      Array.isArray(p.revisions) || typeof p.can_undo === "boolean",
    canUndo: p.can_undo === true,
    revisions: (Array.isArray(p.revisions) ? p.revisions : [])
      .map(readRevision)
      .filter(Boolean),
  };
}

// The window Learn sends for the points lo..hi (indexes into the timeline's
// points, oldest first): from midway between the first chosen burst and the
// one before it to midway between the last and the one after, so a burst
// counts whichever of its times the server goes by; an end without a
// neighbour gets half the usual spacing (at least a minute, at most 30).
export function learnWindow(points, lo, hi, { now = Date.now() } = {}) {
  const list = points || [];
  const a = Math.max(0, Math.min(lo, hi));
  const b = Math.min(list.length - 1, Math.max(lo, hi));
  if (!list.length || b < a) return null;
  const gaps = list
    .slice(1)
    .map((p, i) => p.ms - list[i].ms)
    .filter((g) => g > 0)
    .sort((x, y) => x - y);
  const typical = gaps.length ? gaps[Math.floor(gaps.length / 2)] : 120000;
  const pad = Math.min(1800000, Math.max(60000, typical / 2));
  const first = list[a].ms;
  const last = list[b].ms;
  return {
    from: a > 0 ? (list[a - 1].ms + first) / 2 : first - pad,
    // Never in the future: the server refuses that.
    to: Math.min(
      b < list.length - 1 ? (last + list[b + 1].ms) / 2 : last + pad,
      Math.max(last, now),
    ),
    count: b - a + 1,
    first,
    last,
    states: list
      .slice(a, b + 1)
      .reduce(
        (tally, p) => ({ ...tally, [p.state]: (tally[p.state] || 0) + 1 }),
        {},
      ),
  };
}

// What the server takes (POST radar/profile/): pipe_length_mm 100-5000
// (required), expected_floor_mm 50-5000 (optional), notes up to 500
// characters (optional) and, to commission the node, dry_from and dry_to.
export const PIPE_MM = [100, 5000];
export const FLOOR_MM = [50, 5000];
export const NOTES_MAX = 500;
// What Learn (or Save) sends: the lengths in whole millimetres, the notes
// when there are any and, when commissioning, the window as ISO times.
// -> {body} or {error}
export function learnPayload({
  pipeText = "",
  floorText = "",
  notesText = "",
  window = null,
  commission = true,
}) {
  const read = (text, name, [lo, hi], required) => {
    const s = String(text ?? "").trim();
    if (!s)
      return required
        ? {
            error: `${name}: enter whole millimetres from ${lo} to ${hi.toLocaleString()}.`,
          }
        : { value: null };
    if (!/^\d+$/.test(s) || Number(s) < lo || Number(s) > hi)
      return {
        error: `${name}: whole millimetres from ${lo} to ${hi.toLocaleString()}${required ? "" : ", or leave it blank"}.`,
      };
    return { value: Number(s) };
  };
  const pipe = read(pipeText, "Pipe length", PIPE_MM, true);
  const floor = read(floorText, "Expected floor", FLOOR_MM, false);
  if (pipe.error || floor.error) return { error: pipe.error || floor.error };
  const notes = String(notesText ?? "").trim();
  if (notes.length > NOTES_MAX)
    return { error: `Notes: at most ${NOTES_MAX} characters.` };
  const body = {
    pipe_length_mm: pipe.value,
    ...(floor.value !== null ? { expected_floor_mm: floor.value } : {}),
    ...(notes ? { notes } : {}),
  };
  if (!commission) return { body };
  if (
    !window ||
    !Number.isFinite(window.from) ||
    !Number.isFinite(window.to) ||
    window.to <= window.from
  )
    return { error: "Choose the bursts taken while the pipe was empty." };
  return {
    body: {
      ...body,
      dry_from: new Date(Math.floor(window.from)).toISOString(),
      dry_to: new Date(Math.ceil(window.to)).toISOString(),
    },
  };
}

// ---- Rich-data costs (Node Control, Before you apply) ----

export const RICH_LINES = [
  ["rich", "Rich readings (every wake)"],
  ["burst", "Radar bursts"],
  ["stream", "Radar streaming (live mode)"],
];
const LINE_KINDS = {
  stream: "stream",
  streaming: "stream",
  live: "stream",
  burst: "burst",
  bursts: "burst",
  rich: "rich",
  rich_readings: "rich",
};
// A number, or {low, high}, as {low, high}; null without a value.
function amount(v) {
  if (v && typeof v === "object") {
    const low = finite(v.low);
    const high = finite(v.high);
    return low === null && high === null
      ? null
      : { low: low ?? high, high: high ?? low };
  }
  const n = finite(v);
  return n === null ? null : { low: n, high: n };
}
const word = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);
// The projection's radar lines (`radar`: one per kind switched on, already
// in its totals): [{key ("rich" | "burst" | "stream"), label, mahDay, mbDay,
// mbMonth, mbLiveHour, batterySource ("measured" | "model" |
// "external_power"), dataSource, perDay (bursts a day; rich readings a day),
// perLiveHour, liveHoursDay, captures (a burst's; a normal wake's for rich
// readings), capture (what each holds), secondsEach, mahEach (a burst's),
// measuredBursts, currentMa, currentSource, bytesEach (a burst's or a
// capture's), daylightShare, daylightSource, note} and, for rich readings,
// capturesUpload and captureUpload (an upload wake's captures, all of them),
// capturesDay, wakesDay, uploadWakesDay, minBv (mV) and mahCapture (a
// capture's); from the backend's .62 support, on every line, the node's own
// estimate (edge: radar_edge, edgeMahDay, edgeBytesDay: in the line's battery
// and data) and, on the rich line, uploadsDay (the capture messages sent,
// which its data and sends follow) and keep (the hybrid upload: {every, mm,
// db, share (of rich wakes whose raw captures go up), shareSource ("model" |
// "measured"), readings}, null while off)]; [] when nothing is on; null when
// the projection has no radar lines at all (a server before them).
const readLineKeep = (k) =>
  k && typeof k === "object" && finite(k.share) !== null
    ? {
        every: finite(k.every),
        mm: finite(k.mm),
        db: finite(k.db),
        share: finite(k.share),
        shareSource: word(k.share_source),
        readings: finite(k.readings),
      }
    : null;
export function richDataLines(projection) {
  const raw = projection?.radar ?? projection?.radar_rich;
  if (!raw || typeof raw !== "object") return null;
  const entries = Array.isArray(raw)
    ? raw.map((line) => [line?.kind ?? line?.key, line])
    : Object.entries(raw);
  return entries
    .filter(([, line]) => line && typeof line === "object")
    .map(([key, line]) => {
      const k = String(key ?? "")
        .trim()
        .toLowerCase();
      return {
        key: LINE_KINDS[k] || k || "other",
        label: word(line.label),
        mahDay: amount(line.battery_mah_day),
        mbDay: amount(line.data_mb_day),
        mbMonth: amount(line.data_mb_month),
        mbLiveHour: finite(line.data_mb_live_hour),
        batterySource: word(line.battery_source),
        dataSource: word(line.data_source),
        perDay: finite(line.per_day),
        perLiveHour: finite(line.per_live_hour),
        liveHoursDay: finite(line.live_hours_day),
        captures: finite(line.captures),
        capture: word(line.capture),
        capturesUpload: finite(line.captures_upload),
        captureUpload: word(line.capture_upload),
        capturesDay: finite(line.captures_day),
        wakesDay: finite(line.wakes_day),
        uploadWakesDay: finite(line.upload_wakes_day),
        every: finite(line.every),
        minBv: finite(line.min_bv),
        mahCapture: finite(line.mah_capture),
        // The backend's .59 energy model (padihat-backend #31): the energy
        // meter's hourly baseline, and over LTE each capture message's send.
        baselineMahDay: finite(line.baseline_mah_day),
        sendMahDay: finite(line.send_mah_day),
        // Firmware .60: the radar's rate the line rests on (posted, the
        // node's read-back or the factory 9600: baud_source), a modelled
        // rich capture's time at it, and the stream's interval used (at
        // least 10 s below 38,400 baud).
        baud: finite(line.baud),
        baudSource: word(line.baud_source),
        captureS: finite(line.capture_s),
        streamS: finite(line.stream_s),
        secondsEach: finite(line.seconds_each),
        mahEach: finite(line.mah_each),
        measuredBursts: finite(line.measured_bursts),
        currentMa: finite(line.current_ma),
        currentSource: word(line.current_source),
        bytesEach: finite(line.bytes_each),
        daylightShare: finite(line.daylight_share),
        daylightSource: word(line.daylight_source),
        edge: finite(line.edge),
        edgeMahDay: finite(line.edge_mah_day),
        edgeBytesDay: finite(line.edge_bytes_day),
        uploadsDay: finite(line.uploads_day),
        keep: readLineKeep(line.keep),
        note: word(line.note),
        off: false,
        source: "server",
      };
    });
}

// Our own estimate of the same lines, for a projection without them: data
// from the message sizes the firmware sends (rich readings at the wake and
// upload cadence; the stream's per hour of live mode, a whole day of it;
// bursts at the upload cadence), the battery not estimated (the radar's time
// instead). `plan`: the rich-data settings as settings keys (radar_rich,
// radar_rich_up, radar_stream, radar_stream_s, radar_burst, radar_burst_n,
// radar_burst_gap_s, radar_burst_every, radar_raw_mode, radar_raw_alt).
export function estimateRichData(
  plan,
  { uploadSeconds = null, wakeSeconds = null } = {},
) {
  const lines = [];
  const rich = readWhole(plan?.radar_rich, RICH_RULES.radar_rich);
  if (rich !== null) {
    const up = readWhole(plan?.radar_rich_up, RICH_RULES.radar_rich_up) ?? 0;
    const every =
      readWhole(plan?.radar_rich_every, RICH_RULES.radar_rich_every) ?? 1;
    const on = capturesPerWake(rich) + uploadExtras(rich, up) > 0;
    const est = on
      ? richEstimate({ mask: rich, up, every, wakeSeconds, uploadSeconds })
      : null;
    lines.push({
      key: "rich",
      label: null,
      off: !on,
      mahDay: null,
      mbDay: est ? amount(est.day / 1e6) : null,
      mbMonth: est ? amount(est.month / 1e6) : null,
      radarSeconds: est ? est.radarSecondsWake : null,
      radarDay: est ? est.radarDay : null,
      perDay: est ? est.perDay : null,
      source: "estimate",
      note: null,
    });
  }
  const mode = readRawMode(plan?.radar_raw_mode);
  const alt = readRawAlt(plan?.radar_raw_alt) === 1;
  const burstMask = readMask(plan?.radar_burst);
  if (burstMask !== null) {
    const on = (burstMask & ~8) > 0;
    const est = on
      ? burstEstimate({
          mask: burstMask,
          mode,
          alt,
          n: plan.radar_burst_n,
          gap: plan.radar_burst_gap_s,
          every: plan.radar_burst_every,
          uploadSeconds,
        })
      : null;
    lines.push({
      key: "burst",
      label: null,
      off: !on,
      mahDay: null,
      mbDay: est ? amount(est.day / 1e6) : null,
      mbMonth: est ? amount(est.month / 1e6) : null,
      radarSeconds: est ? est.radarSeconds : null,
      radarDay: est ? est.radarDay : null,
      perDay: est ? est.perDay : null,
      source: "estimate",
      note: null,
    });
  }
  const streamMask = readMask(plan?.radar_stream);
  if (streamMask !== null) {
    const on = (streamMask & ~8) > 0;
    const est = on ? streamEstimate(streamMask, plan.radar_stream_s) : null;
    lines.push({
      key: "stream",
      label: null,
      off: !on,
      mahDay: null,
      mbDay: est ? amount(est.day / 1e6) : null,
      mbMonth: est ? amount((est.day * 30) / 1e6) : null,
      mbHour: est ? est.hour / 1e6 : null,
      source: "estimate",
      note: null,
    });
  }
  return lines;
}

// "0.85 MB", "0.8-1.2 MB", "62 kB"; null without a value.
export function megabytes(range) {
  if (!range) return null;
  const one = (v) =>
    v < 0.1
      ? `${Math.max(0.1, v * 1000).toLocaleString(undefined, { maximumFractionDigits: v < 0.01 ? 1 : 0 })} kB`
      : `${v.toLocaleString(undefined, { maximumFractionDigits: v < 10 ? 2 : v < 100 ? 1 : 0 })} MB`;
  return Math.abs(range.high - range.low) < 1e-9
    ? one(range.low)
    : `${one(range.low)}–${one(range.high)}`;
}
// "4.2 mAh", "3-5 mAh"; null without a value.
export function milliampHours(range) {
  if (!range) return null;
  const one = (v) =>
    v.toLocaleString(undefined, { maximumFractionDigits: v < 10 ? 1 : 0 });
  return Math.abs(range.high - range.low) < 1e-9
    ? `${one(range.low)} mAh`
    : `${one(range.low)}–${one(range.high)} mAh`;
}

// The radar keys the projection reads, as the settings endpoint checks them
// (a server that does not read one leaves it aside).
export const PLAN_KEYS = [
  "radar_rich",
  "radar_rich_up",
  "radar_rich_min_bv",
  "radar_rich_every",
  "radar_burst",
  "radar_burst_n",
  "radar_burst_gap_s",
  "radar_burst_every",
  "radar_burst_min_si",
  "radar_raw_alt",
  "radar_stream",
  "radar_stream_s",
  // Firmware .60: the radar's rate, a set; a modelled capture's time scales
  // with it.
  "radar_baud",
  // Firmware .61-.62: the node's own estimate of each beat signal and .62's
  // hybrid upload (a server before them leaves them aside).
  "radar_edge",
  "radar_keep_every",
  "radar_keep_mm",
  "radar_keep_db",
];
// The Radar rich data card's plan ({rich, stream, burst, raw, baud, edge},
// each settings keys) as the projection takes it: the keys it reads, whole
// numbers within the server's rules (`rules`, the settings endpoint's; a key
// named in `setKeys`, or a rule of other than two entries, is a set of
// values), since it refuses the whole estimate over one that is not.
export function richPlanSettings(plan, rules = null, setKeys = null) {
  const all = {
    ...(plan?.rich || {}),
    ...(plan?.stream || {}),
    ...(plan?.burst || {}),
    ...(plan?.raw || {}),
    ...(plan?.baud || {}),
    ...(plan?.edge || {}),
  };
  return Object.fromEntries(
    PLAN_KEYS.filter((key) => {
      const v = all[key];
      if (!Number.isInteger(v)) return false;
      const set = ruleSet(rules, key, setKeys);
      if (set) return set.map(Number).includes(v);
      const rule = Array.isArray(rules?.[key]) ? rules[key] : null;
      return !rule || (v >= rule[0] && v <= rule[1]);
    }).map((key) => [key, all[key]]),
  );
}
